import * as React from 'react';
import { BaSeTheme } from '../../theme';
import {
  ThemeColorValue,
  ThemeShadowsInterface,
} from '../../theme/theme-interface';
import { BaSeShapeButton } from '../button/shape-button/shape-button';
import { BaSeHeading5 } from '../typography/heading/heading5';
import {
  AccordionContainer,
  AccordionContainerWrapper,
  AccordionContent,
  AccordionOption,
  AccordionTitleContainer,
  AcordionButtonWraper,
} from './accordion-styled';

export interface AccordionOption {
  title: string | JSX.Element;
  content: JSX.Element;
  noPaddingContent?: boolean;
  id?: string | number;
}

export interface AccordionProps {
  options: AccordionOption[];
  backgroundTitleColor?: ThemeColorValue;
  borderTitleColor?: ThemeColorValue;
  backgroundContentColor?: ThemeColorValue;
  titleColor?: string;
  shadow?: ThemeShadowsInterface[keyof ThemeShadowsInterface];
  radius?: number | string;
  multipleOpen?: boolean;
  startOpen?: number | number[];
  hasContentBorder?: boolean;
}

export const BaSeAccordion: React.FC<AccordionProps> = ({
  options = [],
  backgroundTitleColor = BaSeTheme.colors.defaultColors.white,
  backgroundContentColor = BaSeTheme.colors.institucionais.cinzaSebrae97,
  titleColor = BaSeTheme.colors.institucionais.cinzaSebrae45,
  borderTitleColor = BaSeTheme.colors.institucionais.cinzaSebrae97,
  shadow = BaSeTheme.shadows.profundidade2,
  radius = 8,
  multipleOpen = false,
  startOpen: initialStartOpen,
  hasContentBorder = true,
}) => {
  const startOpen = React.useMemo(
    () =>
      Array.isArray(initialStartOpen)
        ? initialStartOpen
        : typeof initialStartOpen === 'number'
        ? [initialStartOpen]
        : undefined,
    [initialStartOpen],
  );

  const getId = React.useCallback((item: AccordionOption, index: number) => {
    const titleAsid = typeof item?.title === 'string' ? item?.title : null;
    return item?.id ?? titleAsid ?? index;
  }, []);

  const [selecteds, setSelecteds] = React.useState<number[]>([]);

  const hasAnyOpen = React.useMemo(() => selecteds.length > 0, [selecteds]);

  const isOpen = React.useCallback(
    (index) => selecteds.includes(index),
    [selecteds],
  );

  const getTitleElement = React.useCallback(
    (element: string | JSX.Element) => {
      if (typeof element === 'string') {
        return <BaSeHeading5 color={titleColor}>{element}</BaSeHeading5>;
      }
      return element;
    },
    [titleColor],
  );

  const handleSingleSelection = React.useCallback(
    (values: number[], index: number) => {
      return values.includes(index) ? [] : [index];
    },
    [],
  );

  const handleMultiSelection = React.useCallback(
    (values: number[], index: number) => {
      if (values.includes(index)) {
        return values.filter((v) => v !== index);
      }
      return [...values, index];
    },
    [],
  );

  const handleUpdate = React.useCallback(
    (values: number[], index: number) => {
      return multipleOpen
        ? handleMultiSelection(values, index)
        : handleSingleSelection(values, index);
    },
    [options, multipleOpen, handleMultiSelection, handleSingleSelection],
  );

  React.useEffect(() => {
    if (!startOpen) {
      return setSelecteds([]);
    }

    if (!multipleOpen && startOpen?.length > 1) {
      throw Error(
        '[BaSeAccordion] Para passar mais de um valor para a propriedade "startOpen", é necessário definir a propriedade "multipleOpen" como true',
      );
    }
    if (startOpen?.length > options.length) {
      throw Error(
        '[BaSeAccordion] Não é possível passar uma quantidade no "startOpen" maior que a quantidade de itens do "Accordion"',
      );
    }
    startOpen.forEach((item) => {
      if (item > options.length - 1) {
        throw Error(
          '[BaSeAccordion] Não é possível inserir um valor de índice no "startOpen" que não existe no "Accordion"',
        );
      }
    });
    setSelecteds(startOpen);
  }, [startOpen, multipleOpen, options]);

  return (
    <AccordionContainer
      shadow={shadow}
      radius={radius}
      isSingle={options.length === 1}
      isOpen={hasAnyOpen}
    >
      {options.map((data, index) => {
        const open = isOpen(index);
        return (
          <AccordionContainerWrapper isOpen={open} key={getId(data, index)}>
            {multipleOpen || options.length === 1 ? (
              <AccordionOption
                type="checkbox"
                id={`BaSe__${getId(data, index)}`}
                checked={open}
                onChange={() =>
                  setSelecteds((values) => handleUpdate(values, index))
                }
              />
            ) : (
              <AccordionOption
                type="radio"
                name="BaSe--accordion-option"
                id={`BaSe__${getId(data, index)}`}
                checked={open}
                onChange={() => {}}
                onClick={() => {
                  setSelecteds((values) => handleUpdate(values, index));
                }}
              />
            )}
            <AccordionTitleContainer
              isFirst={index === 0}
              isLast={index === options?.length - 1}
              isSingle={options?.length === 1}
              isOpen={open}
              headerColor={titleColor}
              borderHeaderColor={borderTitleColor}
              backgroundHeaderColor={backgroundTitleColor}
              radius={radius}
            >
              {getTitleElement(data.title)}
              <AcordionButtonWraper isOpen={open}>
                <BaSeShapeButton
                  color={titleColor}
                  type="tertiary"
                  size="big"
                  nameIcon="angle-down"
                  sizeIcon="big"
                  onClick={() => {
                    setSelecteds((values) => handleUpdate(values, index));
                  }}
                />
              </AcordionButtonWraper>
            </AccordionTitleContainer>
            {open ? (
              <AccordionContent
                hasBorder={hasContentBorder}
                backgroundColor={backgroundContentColor}
                noPaddingContent={data?.noPaddingContent}
                isOpen={open}
                isLast={index === options?.length - 1}
                borderHeaderColor={borderTitleColor}
                radius={radius}
              >
                {data.content}
              </AccordionContent>
            ) : null}
          </AccordionContainerWrapper>
        );
      })}
    </AccordionContainer>
  );
};

BaSeAccordion.displayName = 'BaSeAccordion';
